import type { Draft, EditTarget } from '../../src/shared/types.js';
import type { Issue } from '../../src/shared/validate.js';

declare const __DEMO__: boolean;
export const DEMO = typeof __DEMO__ !== 'undefined' && __DEMO__;

export interface Me {
  user: { id: string; username: string; globalName: string | null; avatarUrl: string } | null;
  oauth: boolean;
  inviteUrl: string | null;
  ready: boolean;
}

export interface GuildSummary {
  id: string;
  name: string;
  iconUrl: string | null;
  canManage: boolean;
}

export interface ChannelInfo {
  id: string;
  name: string;
  type: number;
  parentId: string | null;
  position: number;
  canView: boolean;
  userCanSend: boolean;
  botCanSend: boolean;
  botCanWebhook: boolean;
  tags: { id: string; name: string; emoji: string | null }[];
}

export interface RoleInfo {
  id: string;
  name: string;
  color: number;
  position: number;
  managed: boolean;
  everyone: boolean;
}

export interface TemplateMeta {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: number;
  updatedBy: string | null;
  updatedAt: number;
  mode: Draft['mode'];
  folderId: string | null;
}

export interface Template extends TemplateMeta {
  data: Draft;
  canEdit?: boolean;
}

export interface FolderInput {
  name: string;
  emoji: string | null;
  /** null - все, у кого есть доступ к редактору; [] - только управляющие папками */
  viewRoles: string[] | null;
  /** null - все, кто видит папку; [] - только управляющие папками */
  editRoles: string[] | null;
}

export interface TemplateFolder extends FolderInput {
  id: string;
  sort: number;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
  canView: boolean;
  canEdit: boolean;
}

export interface GuildSettings {
  allowedRoles: string[];
  logChannelId: string | null;
  folderAdminRoles: string[];
}

export interface GuildData {
  id: string;
  name: string;
  iconUrl: string | null;
  canManage: boolean;
  canManageRoles: boolean;
  userTopRole: number;
  botTopRole: number;
  bot: { id: string; username: string; avatarUrl: string };
  me: { id: string; username: string; avatarUrl: string };
  channels: ChannelInfo[];
  threads: { id: string; name: string; parentId: string | null; type: number }[];
  roles: RoleInfo[];
  emojis: { id: string; name: string; animated: boolean }[];
  settings: GuildSettings | null;
  templates: TemplateMeta[];
  folders: TemplateFolder[];
  canManageFolders: boolean;
}

export interface SendTarget {
  channelId: string;
  threadId?: string;
  threadName?: string;
  tagIds?: string[];
  via: 'bot' | 'webhook';
}

export interface SendResult {
  messageId: string;
  channelId: string;
  url: string;
  editTarget: EditTarget | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues: Issue[] = [],
  ) {
    super(message);
  }
}

type Transport = (method: string, path: string, body?: unknown, raw?: Blob) => Promise<any>;

const httpTransport: Transport = async (method, path, body, raw) => {
  const headers: Record<string, string> = { 'X-Requested-With': 'embed-studio' };
  let payload: BodyInit | undefined;
  if (raw) {
    payload = raw;
    headers['Content-Type'] = raw.type || 'application/octet-stream';
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin' });
  if (res.status === 204) return undefined;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error || `Ошибка ${res.status}`, data.issues || []);
  return data;
};

let transport: Transport = httpTransport;
export function setTransport(t: Transport) {
  transport = t;
}

export const api = {
  me: (): Promise<Me> => transport('GET', '/api/me'),
  logout: () => transport('POST', '/api/logout', {}),
  guilds: (): Promise<GuildSummary[]> => transport('GET', '/api/guilds'),
  guild: (id: string): Promise<GuildData> => transport('GET', `/api/guilds/${id}`),
  saveSettings: (g: string, s: GuildSettings) =>
    transport('PUT', `/api/guilds/${g}/settings`, s),
  templates: (g: string): Promise<TemplateMeta[]> => transport('GET', `/api/guilds/${g}/templates`),
  template: (g: string, id: string): Promise<Template> => transport('GET', `/api/guilds/${g}/templates/${id}`),
  createTemplate: (g: string, name: string, data: Draft, folderId: string | null = null): Promise<Template> =>
    transport('POST', `/api/guilds/${g}/templates`, { name, data, folderId }),
  updateTemplate: (g: string, id: string, patch: { name?: string; data?: Draft; folderId?: string | null }): Promise<Template> =>
    transport('PUT', `/api/guilds/${g}/templates/${id}`, patch),
  deleteTemplate: (g: string, id: string) => transport('DELETE', `/api/guilds/${g}/templates/${id}`),
  folders: (g: string): Promise<TemplateFolder[]> => transport('GET', `/api/guilds/${g}/folders`),
  createFolder: (g: string, f: FolderInput): Promise<TemplateFolder> => transport('POST', `/api/guilds/${g}/folders`, f),
  updateFolder: (g: string, id: string, patch: Partial<FolderInput>): Promise<TemplateFolder> =>
    transport('PUT', `/api/guilds/${g}/folders/${id}`, patch),
  deleteFolder: (g: string, id: string): Promise<{ ok: true; moved: number }> => transport('DELETE', `/api/guilds/${g}/folders/${id}`),
  draft: (g: string, id: string): Promise<{ data: Draft; editTarget: EditTarget | null; warnings: string[] }> =>
    transport('GET', `/api/guilds/${g}/drafts/${id}`),
  loadMessage: (g: string, link: string): Promise<{ draft: Draft; warnings: string[]; editTarget: EditTarget | null }> =>
    transport('POST', `/api/guilds/${g}/load-message`, { link }),
  send: (g: string, draft: Draft, target?: SendTarget, edit?: EditTarget): Promise<SendResult> =>
    transport('POST', `/api/guilds/${g}/send`, { draft, target, edit }),
  upload: (file: File): Promise<{ url: string; preview: string; name: string; size: number; mime: string }> =>
    transport('POST', `/api/upload?name=${encodeURIComponent(file.name)}`, undefined, file),
};

/** Превращает upload://id/name в адрес для предпросмотра. */
export function previewUrl(url: string): string {
  if (url.startsWith('upload://')) {
    const rest = url.slice('upload://'.length);
    if (DEMO) return demoUploads.get(rest.split('/')[0]) ?? '';
    return `/uploads/${rest}`;
  }
  return url;
}

/** В демо-режиме файлы живут в памяти браузера как blob-ссылки. */
export const demoUploads = new Map<string, string>();
